import type { Scenario } from '../types'

/**
 * The Mandarin second wave, matching what the other four languages carry:
 * situations where getting it wrong costs you something rather than merely
 * embarrassing you.
 *
 * Prompts are the other person's lines, so they are pitched slightly above the
 * blocks you answer with — that gap is the point. Real speakers do not
 * moderate to your level, and a scenario that only ever speaks in sentences you
 * already own is not practising anything.
 */

const SEED_CLOCK = 0
const freshStats = () => ({ runs: 0, bestScore: 0, lastScore: null, lastRunAt: null })

interface Beat {
  speaker: string
  hanzi: string
  pinyin: string
  english: string
  cue: string
  island: string
  blocks: string[]
  hint?: string
}

interface Seed {
  slug: string
  title: string
  titleZh: string
  setting: string
  brief: string
  beats: Beat[]
}

const SEEDS: Seed[] = [
  {
    slug: 'huochezhan',
    title: 'Making the connection',
    titleZh: '在火车站',
    setting: '北京南站，离开车还有十一分钟，屏幕上什么也看不清',
    brief:
      'Eleven minutes to departure and the boards are no help. The clerk is helpful but quick, and every fumbled question costs a minute you do not have.',
    beats: [
      {
        speaker: '售票员',
        hanzi: '您好，您要买什么票？',
        pinyin: 'nín hǎo, nín yào mǎi shén me piào?',
        english: 'Hello, what ticket would you like?',
        cue: 'Buy a return to Shanghai.',
        island: 'isl_wenlu',
        blocks: ['isl_wenlu_b3'],
        hint: '张 is the measure word for a ticket.',
      },
      {
        speaker: '售票员',
        hanzi: '往返票，好的。还需要别的吗？',
        pinyin: 'wǎng fǎn piào, hǎo de. hái xū yào bié de ma?',
        english: 'A return, right. Anything else?',
        cue: 'You still do not know the platform.',
        island: 'isl_wenlu',
        blocks: ['isl_wenlu_b4'],
      },
      {
        speaker: '售票员',
        hanzi: '九号站台。您快点儿，马上就要开了。',
        pinyin: 'jiǔ hào zhàn tái. nín kuài diǎnr, mǎ shàng jiù yào kāi le.',
        english: 'Platform nine. Be quick, it is about to leave.',
        cue: 'You have no idea which way that is.',
        island: 'isl_wenlu',
        blocks: ['isl_wenlu_b1'],
      },
      {
        speaker: '旅客',
        hanzi: '您好像找不到路，您要去哪儿？',
        pinyin: 'nín hǎo xiàng zhǎo bu dào lù, nín yào qù nǎr?',
        english: 'You look lost — where are you trying to get to?',
        cue: 'Ask her to give you a shout at your stop.',
        island: 'isl_wenlu',
        blocks: ['isl_wenlu_b5'],
      },
    ],
  },
  {
    slug: 'shangdian',
    title: 'Buying something that fits',
    titleZh: '在商店',
    setting: '一家商场，周六下午，售货员马上就过来了',
    brief:
      'A department store on a Saturday. The assistant reaches you within seconds, so the "just looking" line has to be ready before you have decided anything.',
    beats: [
      {
        speaker: '售货员',
        hanzi: '您好，需要帮忙吗？',
        pinyin: 'nín hǎo, xū yào bāng máng ma?',
        english: 'Hello, do you need any help?',
        cue: 'Buy yourself a minute.',
        island: 'isl_maidongxi',
        blocks: ['isl_maidongxi_b2'],
      },
      {
        speaker: '售货员',
        hanzi: '好的，您慢慢看。',
        pinyin: 'hǎo de, nín màn màn kàn.',
        english: 'Of course, take your time.',
        cue: 'This one is too small.',
        island: 'isl_maidongxi',
        blocks: ['isl_maidongxi_b3'],
      },
      {
        speaker: '售货员',
        hanzi: '有的，我给您拿一件大的。',
        pinyin: 'yǒu de, wǒ gěi nín ná yí jiàn dà de.',
        english: 'We do — let me get you a larger one.',
        cue: 'Find out what it costs first.',
        island: 'isl_maidongxi',
        blocks: ['isl_maidongxi_b1'],
      },
      {
        speaker: '售货员',
        hanzi: '三百八十块。您怎么付？',
        pinyin: 'sān bǎi bā shí kuài. nín zěn me fù?',
        english: 'Three hundred and eighty. How will you pay?',
        cue: 'You have no cash.',
        island: 'isl_maidongxi',
        blocks: ['isl_maidongxi_b4'],
      },
    ],
  },
  {
    slug: 'chuwenti',
    title: 'Something went wrong',
    titleZh: '出了问题',
    setting: '客服台，周一上午，后面排队的人越来越多',
    brief:
      'Customer service on a Monday with a queue growing behind you. Complaining in a second language is where politeness and clarity pull against each other — these blocks do both at once.',
    beats: [
      {
        speaker: '客服',
        hanzi: '下一位。您有什么事？',
        pinyin: 'xià yí wèi. nín yǒu shén me shì?',
        english: 'Next. What can I do for you?',
        cue: 'Open without accusing anyone.',
        island: 'isl_chuwenti',
        blocks: ['isl_chuwenti_b1'],
        hint: '好像 softens it to "seems" rather than "is".',
      },
      {
        speaker: '客服',
        hanzi: '具体是什么问题呢？',
        pinyin: 'jù tǐ shì shén me wèn tí ne?',
        english: 'What exactly is the problem?',
        cue: 'State the facts plainly.',
        island: 'isl_chuwenti',
        blocks: ['isl_chuwenti_b2'],
      },
      {
        speaker: '客服',
        hanzi: '我得先查一下系统，看看到底发出去了没有。',
        pinyin: 'wǒ děi xiān chá yí xià xì tǒng, kàn kan dào dǐ fā chū qù le méi yǒu.',
        english: 'I will have to check the system first to see whether it ever went out.',
        cue: 'You lost that. Ask her to slow down.',
        island: 'isl_chuwenti',
        blocks: ['isl_chuwenti_b5'],
      },
      {
        speaker: '客服',
        hanzi: '不好意思。我查一下发货记录。',
        pinyin: 'bù hǎo yì si. wǒ chá yí xià fā huò jì lù.',
        english: 'Sorry. Let me check the dispatch record.',
        cue: 'Ask for her help directly.',
        island: 'isl_chuwenti',
        blocks: ['isl_chuwenti_b3'],
      },
    ],
  },
]

export function buildMoreZhScenarios(): Scenario[] {
  return SEEDS.map((seed) => ({
    id: `scn_zh_${seed.slug}`,
    language: 'zh',
    title: seed.title,
    titleZh: seed.titleZh,
    setting: seed.setting,
    brief: seed.brief,
    isStarter: true,
    updatedAt: SEED_CLOCK,
    stats: freshStats(),
    beats: seed.beats.map((beat, i) => ({
      id: `scn_zh_${seed.slug}_b${i + 1}`,
      speaker: beat.speaker,
      promptHanzi: beat.hanzi,
      promptPinyin: beat.pinyin,
      promptEnglish: beat.english,
      cue: beat.cue,
      expectIslandId: beat.island,
      expectBlockIds: beat.blocks,
      ...(beat.hint ? { hint: beat.hint } : {}),
    })),
  }))
}
